import {
    getData, postData, putData, deleteData
} from "../apiClient";

const API_URL = `assigned-roster`;

/**
 * ============================================================================
 * ASSIGNED ROSTER LIST API (menu_id: 99)
 * ============================================================================
 * APIs for viewing roster assignments and details
 * ============================================================================
 */

/**
 * Get all rosters with pagination
 * @param {Object} params - Query parameters
 * @param {number} params.page - Page number
 * @param {number} params.limit - Items per page
 * @param {string} params.search - Search term
 * @param {AbortSignal} signal - Abort signal for cancellation
 */
export const GetAllRoster = async ({ page = 1, limit = 10, search = "" }, signal = null) => {
    try {
        const queryParams = new URLSearchParams({
            page: String(page),
            limit: String(limit),
            search: search,
        });

        const result = await getData(`${API_URL}/list?${queryParams}`, {}, {}, signal);
        return {
            success: true,
            ...result,
        };
    } catch (error) {
        console.error("Get Roster List API error:", error);
        return {
            success: false,
            ...error,
        };
    }
};

/**
 * Get all active rosters for dropdown (simple list)
 * @param {AbortSignal} signal - Abort signal for cancellation
 */
export const GetAllRosterList = async (signal = null) => {
    try {
        const result = await getData(`${API_URL}/roster-list`, {}, {}, signal);
        return {
            success: true,
            data: result,
        };
    } catch (error) {
        console.error("Get Roster List API error:", error);
        return {
            data: {
                success: false,
                error: error.status === 400 ? error.data?.error : error.message,
                status: error.status,
            },
        };
    }
};

/**
 * Create a new roster
 */
export const CreateRoster = async ({ data }) => {
    try {
        const result = await postData(`${API_URL}/add`, data);
        return {
            data: {
                success: true,
                data: result,
            },
        };
    } catch (error) {
        return {
            data: {
                success: false,
                error: error.status === 400 ? error.data?.error : error.message,
                status: error.status,
            },
        };
    }
};

/**
 * Update an existing roster
 */
export const UpdateRoster = async ({ id, data }) => {
    try {
        const result = await putData(`${API_URL}/update/${id}`, data);
        return {
            data: {
                success: true,
                data: result,
            },
        };
    } catch (error) {
        return {
            data: {
                success: false,
                error: error.status === 400 ? error.data?.error : error.message,
                status: error.status,
            },
        };
    }
};

/**
 * Get a roster by its ID
 */
export const GetRosterById = async (id) => {
    try {
        const result = await getData(`${API_URL}/${id}`);
        return {
            data: {
                success: true,
                ...result,
            },
        };
    } catch (error) {
        return {
            data: {
                success: false,
                error: error.message,
            },
        };
    }
};

/**
 * Delete a roster
 */
export const DeleteRoster = async ({ id }) => {
    try {
        const result = await deleteData(`${API_URL}/delete/${id}`);
        return {
            data: {
                success: true,
                data: result,
            },
        };
    } catch (error) {
        return {
            data: {
                success: false,
                error: error.message,
            },
        };
    }
};

/**
 * Get all available shifts for dropdown
 */
export const getAllshift = async () => {
    try {
        const result = await getData(`${API_URL}/get/all/shift`);
        return {
            data: {
                success: true,
                ...result,
            },
        };
    } catch (error) {
        return {
            data: {
                success: false,
                error: error.status === 400 ? error.error : error.message,
                status: error.status,
            },
        };
    }
};

/**
 * Get paginated list of assigned rosters
 * @param {Object} params - Query parameters
 * @param {number} params.page - Page number
 * @param {number} params.limit - Items per page
 * @param {string} params.search - Search term
 * @param {AbortSignal} signal - Abort signal for cancellation
 */
export const getAssignedRosterList = async ({ page = 1, limit = 10, search = "" }, signal = null) => {
    try {
        const queryParams = new URLSearchParams({ page, limit, search }).toString();
        const response = await getData(`${API_URL}/list?${queryParams}`, {}, {}, signal);
        return {
            success: true,
            data: response?.data || [],
            ...response,
        };
    } catch (error) {
        console.error("Error fetching assigned roster list", error);
        return { success: false, data: [], error: error.message };
    }
};

/**
 * Get assigned roster details with employees
 * @param {number} rosterId - Roster ID
 * @param {AbortSignal} signal - Abort signal for cancellation
 */
export const getAssignedRosterDetail = async (rosterId, signal = null) => {
    try {
        const response = await getData(`${API_URL}/detail/${rosterId}`, {}, {}, signal);
        return {
            success: true,
            data: response?.data || null,
        };
    } catch (error) {
        console.error("Error fetching assigned roster details", error);
        return { success: false, data: null, error: error.message };
    }
};
